import { useState, useEffect } from 'react';

const CORRECT_USERNAME = 'amparo';
const CORRECT_PASSWORD = 'florida3699';
const SESSION_KEY = 'admin_logged_in';

interface Props {
  children: React.ReactNode;
}

export default function AdminPinGate({ children }: Props) {
  const [verified, setVerified] = useState<boolean | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const ok = sessionStorage.getItem(SESSION_KEY) === 'true';
      setVerified(ok);
    } catch {
      setVerified(false);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    setTimeout(() => {
      if (
        username.trim().toLowerCase() === CORRECT_USERNAME &&
        password === CORRECT_PASSWORD
      ) {
        try {
          sessionStorage.setItem(SESSION_KEY, 'true');
        } catch { /* ignore */ }
        setVerified(true);
      } else {
        setError('Incorrect username or password. Please try again.');
        setPassword('');
      }
      setLoading(false);
    }, 500);
  };

  if (verified === null) return null;
  if (verified === true) return <>{children}</>;

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl border border-gray-100 p-10 w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center bg-amber-500 rounded-full mx-auto mb-5">
            <i className="ri-home-2-line text-white text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Listings Manager</h1>
          <p className="text-gray-400 text-sm">Sign in to manage your properties</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Username
            </label>
            <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2.5 focus-within:ring-2 focus-within:ring-amber-400 focus-within:border-transparent transition-all">
              <span className="w-5 h-5 flex items-center justify-center text-gray-400 mr-2">
                <i className="ri-user-line text-sm"></i>
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setError(''); }}
                placeholder="Enter username"
                className="flex-1 outline-none text-sm text-gray-800 bg-transparent"
                autoComplete="username"
                autoFocus
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Password
            </label>
            <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2.5 focus-within:ring-2 focus-within:ring-amber-400 focus-within:border-transparent transition-all">
              <span className="w-5 h-5 flex items-center justify-center text-gray-400 mr-2">
                <i className="ri-lock-line text-sm"></i>
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter password"
                className="flex-1 outline-none text-sm text-gray-800 bg-transparent"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors ml-2"
              >
                <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
              <span className="w-4 h-4 flex items-center justify-center text-red-500">
                <i className="ri-error-warning-line text-sm"></i>
              </span>
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-semibold rounded-lg text-sm cursor-pointer transition-colors whitespace-nowrap mt-2 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Hint */}
        <p className="text-center text-gray-300 text-xs mt-6">
          Florida Houses For Sale &mdash; Admin Access Only
        </p>
      </div>
    </div>
  );
}
